import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type {
  CompanyInfoResponse,
  DashboardHeaderResponse,
  OrganizationLeadEmailResponse,
  PageUserListItem,
  PortalInviteRead,
  UserResponse,
} from './models'
import { serviceClient } from '#/shared/api/http-client'

const BASE = '/auth/admin/api/v1/product'

type QueryOpts<T> = { query?: Partial<UseQueryOptions<AxiosResponse<T>>> & { enabled?: boolean } }

function orgParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return { ...(organization_id ? { organization_id } : {}), ...extra }
}

export function useListUsersApiV1UsersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts<PageUserListItem>,
) {
  const merged = orgParams(params)
  return useQuery<AxiosResponse<PageUserListItem>>({
    queryKey: [`/api/v1/users`, merged],
    queryFn: () => serviceClient.get(`${BASE}/users`, { params: merged }),
    ...options?.query,
  })
}

export function useGetMeApiV1UsersMeGet(options?: QueryOpts<UserResponse>) {
  return useQuery<AxiosResponse<UserResponse>>({
    queryKey: ['admin-auth-me-as-v1-user'],
    queryFn: async () => {
      const response = await serviceClient.get('/auth/admin/api/v1/auth/me')
      const admin = response.data as { id: string; email: string; first_name?: string | null; last_name?: string | null }
      const user: UserResponse = {
        id: admin.id,
        email: admin.email,
        first_name: admin.first_name ?? null,
        last_name: admin.last_name ?? null,
        is_superuser: true,
      }
      return { ...response, data: user }
    },
    ...options?.query,
  })
}

type MutationOpts<V> = { mutation?: UseMutationOptions<AxiosResponse<unknown>, unknown, V> }

export const getListInvitesApiV1PortalInvitesGetQueryKey = (params?: Record<string, unknown>) => [
  `/api/v1/portal/invites`,
  ...(params ? [params] : []),
]

export function useListInvitesApiV1PortalInvitesGet(
  params?: Record<string, unknown>,
  options?: QueryOpts<PortalInviteRead[]>,
) {
  const merged = orgParams(params)
  return useQuery<AxiosResponse<PortalInviteRead[]>>({
    queryKey: [`/api/v1/portal/invites`, merged],
    queryFn: () => serviceClient.get(`${BASE}/portal/invites`, { params: merged }),
    ...options?.query,
  })
}

export function useSendInviteApiV1PortalInvitesPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/portal/invites`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useResendInviteApiV1PortalInvitesInviteIdResendPost(
  options?: MutationOpts<{ inviteId: string }>,
) {
  return useMutation({
    mutationFn: ({ inviteId }: { inviteId: string }) =>
      serviceClient.post(`${BASE}/portal/invites/${inviteId}/resend`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useGetUserApiV1UsersUserIdGet(
  userId: string,
  options?: QueryOpts<UserResponse>,
) {
  return useQuery<AxiosResponse<UserResponse>>({
    queryKey: [`/api/v1/users/${userId}`],
    queryFn: () => serviceClient.get(`${BASE}/users/${userId}`, { params: orgParams() }),
    ...options?.query,
  })
}

export function useGetLeadEmailApiV1LeadEmailGet(
  options?: QueryOpts<OrganizationLeadEmailResponse>,
) {
  const params = orgParams()
  return useQuery<AxiosResponse<OrganizationLeadEmailResponse>>({
    queryKey: [`/api/v1/lead-email`, params],
    queryFn: () => serviceClient.get(`${BASE}/lead-email`, { params }),
    ...options?.query,
  })
}

export function useGetHeader(options?: QueryOpts<DashboardHeaderResponse>) {
  const params = orgParams()
  return useQuery<AxiosResponse<DashboardHeaderResponse>>({
    queryKey: [`/api/v1/dashboard/header`, params],
    queryFn: () => serviceClient.get(`${BASE}/dashboard/header`, { params }),
    ...options?.query,
  })
}

export const getGetCompanyInfoApiV1CompanyInfoGetQueryKey = () => [`/api/v1/company-info`]

export function useGetCompanyInfoApiV1CompanyInfoGet(options?: QueryOpts<CompanyInfoResponse>) {
  return useQuery<AxiosResponse<CompanyInfoResponse>>({
    queryKey: getGetCompanyInfoApiV1CompanyInfoGetQueryKey(),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/company-info`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useUpdateCompanyInfoApiV1CompanyInfoPatch(options?: {
  mutation?: UseMutationOptions<AxiosResponse<unknown>, unknown, { data: unknown }>
}) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.patch(`${BASE}/company-info`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}
